import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronIcon, ThrashIcon } from '~/assets';
import SecondaryButton from '~/components/Button/SecondaryButton';
import Checkbox from '~/components/Checkbox';
import MatchingAttributePicker from '~/components/MatchingAttributePicker';
import { CustomerListMatchingAttribute } from '~/types/api.graphql';
import {
  canAddRule,
  CustomerListColumn,
  CustomerListColumnTypes,
  CustomerListMatchingLimits,
  CustomerListRuleDraft,
  isProfileOnlyRule,
  MatchingRulesIssue,
} from '~/utils/customerListMatchingRules';

import { ErrorLine } from '../visitiqCatalog.styles';
import BackToSimpleConfirmation from './BackToSimpleConfirmation';
import { describeMatchingRulesIssue } from './issueMessages';
import {
  ActionsRow,
  ColumnHint,
  ColumnName,
  ColumnRow,
  FieldContainer,
  HeaderRow,
  HelperText,
  IconButton,
  LinkButton,
  OrSeparator,
  RuleCard,
  RuleHeader,
  Subtitle,
  Title,
  TypePicker,
} from './MatchingRulesField.styles';
import ProfileOnlyWarning from './ProfileOnlyWarning';
import RuleTitle from './RuleTitle';

interface CustomViewProps {
  columns: CustomerListColumn[];
  limits: CustomerListMatchingLimits;
  types: CustomerListColumnTypes;
  rules: CustomerListRuleDraft[];
  issues: MatchingRulesIssue[];
  expandedRule: number | null;
  rulesToDrop: number[] | null;
  onExpandRule: (ruleIndex: number | null) => void;
  onTypeChange: (columnIndex: number, attribute: CustomerListMatchingAttribute | undefined) => void;
  onToggleColumn: (ruleIndex: number, columnIndex: number) => void;
  onAddRule: () => void;
  onDeleteRule: (ruleIndex: number) => void;
  onBackToSimple: () => void;
  onConfirmBackToSimple: () => void;
  onCancelBackToSimple: () => void;
}

/**
 * The rules edited directly: each rule's columns ticked, one type per column across every rule. Picking a type ticks
 * the column in the expanded rule; a rule over the field limit says so instead of blocking the tick.
 */
const CustomView = ({
  columns,
  limits,
  types,
  rules,
  issues,
  expandedRule,
  rulesToDrop,
  onExpandRule,
  onTypeChange,
  onToggleColumn,
  onAddRule,
  onDeleteRule,
  onBackToSimple,
  onConfirmBackToSimple,
  onCancelBackToSimple,
}: CustomViewProps): JSX.Element => {
  const { t } = useTranslation();
  const ruleFields = (rule: CustomerListRuleDraft) =>
    rule.map((index): [number, string, CustomerListMatchingAttribute | undefined] => [
      index,
      columns.find((column) => column.index === index)?.columnName ?? '',
      types[index],
    ]);
  const isAtRuleLimit = !canAddRule(rules, limits);

  return (
    <FieldContainer>
      <div>
        <HeaderRow>
          <Title>{t('aiInsights.a2ui.matchingRules.customTitle')}</Title>
          <Subtitle>
            {t('aiInsights.a2ui.matchingRules.ruleCount', { count: rules.length, max: limits.maxRules })}
          </Subtitle>
        </HeaderRow>
        <Subtitle>{t('aiInsights.a2ui.matchingRules.customSubtitle')}</Subtitle>
      </div>
      {rules.map((rule, ruleIndex) => {
        const isExpanded = expandedRule === ruleIndex;
        const isProfileOnly = isProfileOnlyRule(rule, types, limits.identifierAttributes);
        const ruleIssues = issues.filter((issue) => issue.ruleIndex === ruleIndex);
        return (
          // Rules have no id of their own; their position is what the user edits
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={ruleIndex}>
            {ruleIndex > 0 && <OrSeparator>{t('aiInsights.a2ui.matchingRules.or')}</OrSeparator>}
            <RuleCard $expanded={isExpanded}>
              <RuleHeader>
                <IconButton
                  type="button"
                  aria-label={t('aiInsights.a2ui.matchingRules.expandRule')}
                  aria-expanded={isExpanded}
                  $rotated={isExpanded}
                  onClick={() => onExpandRule(isExpanded ? null : ruleIndex)}
                >
                  <ChevronIcon />
                </IconButton>
                <RuleTitle fields={ruleFields(rule)} />
                <IconButton
                  type="button"
                  $rotated
                  aria-label={t('aiInsights.a2ui.matchingRules.deleteRule')}
                  onClick={() => onDeleteRule(ruleIndex)}
                >
                  <ThrashIcon />
                </IconButton>
              </RuleHeader>
              {isProfileOnly && <ProfileOnlyWarning />}
              {isExpanded && (
                <>
                  <HelperText>{t('aiInsights.a2ui.matchingRules.whichColumns')}</HelperText>
                  {columns.map(({ index, columnName }) => {
                    const isTicked = rule.includes(index);
                    const otherRules = rules.flatMap((other, otherIndex) =>
                      otherIndex !== ruleIndex && other.includes(index) ? [otherIndex + 1] : [],
                    );
                    return (
                      <ColumnRow key={index} $highlighted={isTicked}>
                        <Checkbox
                          aria-label={columnName}
                          isSelected={isTicked}
                          onChange={() => onToggleColumn(ruleIndex, index)}
                        />
                        <ColumnName>
                          {columnName}
                          {otherRules.length > 0 && (
                            <ColumnHint>
                              {t('aiInsights.a2ui.matchingRules.inRules', { rules: otherRules.join(', ') })}
                            </ColumnHint>
                          )}
                        </ColumnName>
                        <TypePicker>
                          <MatchingAttributePicker
                            allowNone
                            attribute={types[index]}
                            identifierAttributes={limits.identifierAttributes}
                            onChange={(selected) => onTypeChange(index, selected)}
                          />
                        </TypePicker>
                      </ColumnRow>
                    );
                  })}
                  <HelperText>{t('aiInsights.a2ui.matchingRules.sharedType')}</HelperText>
                </>
              )}
              {ruleIssues.map((issue) => (
                <ErrorLine key={issue.problem}>{describeMatchingRulesIssue({ t, issue, columns, limits })}</ErrorLine>
              ))}
            </RuleCard>
          </React.Fragment>
        );
      })}
      {issues
        .filter((issue) => issue.ruleIndex === undefined)
        .map((issue) => (
          <ErrorLine key={issue.problem}>{describeMatchingRulesIssue({ t, issue, columns, limits })}</ErrorLine>
        ))}
      <ActionsRow>
        <SecondaryButton auto disableMinWidth disabled={isAtRuleLimit} onPress={onAddRule}>
          {t('aiInsights.a2ui.matchingRules.addRule')}
        </SecondaryButton>
        <LinkButton type="button" onClick={onBackToSimple}>
          {t('aiInsights.a2ui.matchingRules.backToSimple')}
        </LinkButton>
      </ActionsRow>
      {rulesToDrop && (
        <BackToSimpleConfirmation
          droppedRules={rulesToDrop.map((ruleIndex) => ruleFields(rules[ruleIndex]))}
          onConfirm={onConfirmBackToSimple}
          onCancel={onCancelBackToSimple}
        />
      )}
      {isAtRuleLimit && (
        <HelperText>{t('aiInsights.a2ui.matchingRules.ruleLimitReached', { max: limits.maxRules })}</HelperText>
      )}
    </FieldContainer>
  );
};

export default CustomView;
